import { useState } from 'react';
import { Alert, Button, Card, Empty, Flex, notification, Space, Typography } from 'antd';
import { CheckSquareOutlined, EyeOutlined, MinusSquareOutlined, PrinterOutlined } from '@ant-design/icons';

import {
	useGetKittingTagsDataQuery,
	usePreviewSelectedKittingTagsMutation,
	usePrintSelectedKittingTagsMutation,
} from '../../../redux/api/order-line-item';
import { usePrintLocation } from '../../../context/PrintLocationContext';
import KittingTagPicker from '../../../components/Operations/Kitting/KittingTagPicker';
import { useKittingTagSelection } from '../../../components/Operations/Kitting/KittingTagPicker/useKittingTagSelection';
import { kittingWarnings } from '../../../components/Operations/Kitting/kittingWarnings';
import { extractApiError } from '../../../utils/apiError';

const { Title, Text } = Typography;

interface IProps {
	lineItemId: string;
	printDisabled?: boolean;
	/** Tool and jaw tags are built from the setup sheet export. Undefined while
	 *  the exports are still loading, so no notice flashes on first paint. */
	hasSetupSheet?: boolean;
	/** This page only reprints. The first print belongs to the floor's Start
	 *  Kitting, which is what opens the kitting session the reports time. */
	kittingStarted: boolean;
	/** Printing moves the kit to In Progress, so the page should reload the line item. */
	onPrinted: () => void;
}

const LineItemKitting = ({
	lineItemId,
	printDisabled = false,
	hasSetupSheet,
	kittingStarted,
	onPrinted,
}: IProps) => {
	const [notify, notificationContext] = notification.useNotification();
	const { promptForBuilding } = usePrintLocation();
	const { data, isLoading, isError, error, refetch } = useGetKittingTagsDataQuery(lineItemId, {
		refetchOnMountOrArgChange: true,
	});
	const [printSelected, { isLoading: isPrinting }] = usePrintSelectedKittingTagsMutation();
	const [previewSelected, { isLoading: isPreviewing }] = usePreviewSelectedKittingTagsMutation();

	const selection = useKittingTagSelection(data);
	const { totalSelected, allSelected } = selection;
	const [previewError, setPreviewError] = useState<string | null>(null);

	const tagCount = data
		? data.tools.length + data.jaws.length + (data.other_tags?.length ?? 0) + (data.special_tags?.length ?? 0)
		: 0;

	const handlePreview = async () => {
		setPreviewError(null);
		const { toolIndices, jawIndices, otherTagKeys } = selection.selectedTags();
		try {
			const blob = await previewSelected({
				id: lineItemId,
				tool_indices: toolIndices,
				jaw_indices: jawIndices,
				other_tag_keys: otherTagKeys,
			}).unwrap();
			const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
			window.open(url, '_blank');
			setTimeout(() => URL.revokeObjectURL(url), 60_000);
		} catch {
			setPreviewError('Could not generate a preview for the selected tags.');
		}
	};

	const handlePrint = () => {
		const { toolIndices, jawIndices, otherTagKeys } = selection.selectedTags();
		promptForBuilding(
			async (buildingId) => {
				try {
					const result = await printSelected({
						id: lineItemId,
						tool_indices: toolIndices,
						jaw_indices: jawIndices,
						other_tag_keys: otherTagKeys,
						building_id: buildingId || undefined,
					}).unwrap();
					selection.clear();
					onPrinted();
					const warnings = kittingWarnings(result);
					if (warnings.length > 0) {
						notify.warning({
							duration: 10,
							message: 'Printed with missing labels',
							description: warnings.join(' '),
						});
					} else {
						notify.success({
							message: 'Print Successful',
							description: `Sent ${toolIndices.length + jawIndices.length + otherTagKeys.length
								} tag(s) to the printer.`,
						});
					}
				} catch (e) {
					notify.error({
						duration: 10,
						message: 'Error printing selected tags',
						description: extractApiError(e),
					});
				}
			},
			{ description: 'Select the building to print the selected tags at.' },
		);
	};

	const renderBody = () => {
		if (isLoading) {
			return <Card loading />;
		}
		if (isError) {
			return (
				<Alert
					type="error"
					showIcon
					message="Could not load kitting tags"
					description={extractApiError(error)}
					action={
						<Button size="small" onClick={() => refetch()}>
							Retry
						</Button>
					}
				/>
			);
		}
		const toolJawEmptyDescription =
			hasSetupSheet === false ? 'Tool and jaw tags require a setup sheet' : undefined;

		if (tagCount === 0) {
			return (
				<Card>
					<Empty description={toolJawEmptyDescription ?? 'No kitting tags to print.'} />
				</Card>
			);
		}
		return (
			<Card>
				<Flex justify="space-between" align="center" wrap="wrap" gap="small" style={{ marginBottom: 24 }}>
					<Text type="secondary">
						{kittingStarted
							? 'Select the tags you want to preview or print.'
							: 'Kitting has not started. Start kitting from Floor → Kitting before printing tags here. You can still preview them.'}
					</Text>
					<Space wrap>
						<Button
							icon={allSelected ? <MinusSquareOutlined /> : <CheckSquareOutlined />}
							onClick={selection.toggleEverything}
						>
							{allSelected ? 'Deselect All' : 'Select All'}
						</Button>
						<Button
							icon={<EyeOutlined />}
							disabled={totalSelected === 0}
							loading={isPreviewing}
							onClick={handlePreview}
						>
							Preview
						</Button>
						<Button
							type="primary"
							icon={<PrinterOutlined />}
							disabled={totalSelected === 0 || printDisabled || !kittingStarted}
							loading={isPrinting}
							onClick={handlePrint}
						>
							Print ({totalSelected})
						</Button>
					</Space>
				</Flex>
				{previewError && (
					<Alert
						type="error"
						showIcon
						closable
						style={{ marginBottom: 24 }}
						message={previewError}
						onClose={() => setPreviewError(null)}
					/>
				)}
				<KittingTagPicker
					data={data}
					selection={selection}
					layout="three-column"
					toolJawEmptyDescription={toolJawEmptyDescription}
				/>
			</Card>
		);
	};

	return (
		<div id="kitting-section" className="mb-10">
			<Title level={4}>Kitting</Title>
			{renderBody()}
			{notificationContext}
		</div>
	);
};

export default LineItemKitting;
